import {useCallback, useEffect, ReactNode} from 'react';
import {useLocation} from 'react-router-dom';
import {Box, Card, CardContent, Collapse, Divider, IconButton} from '@mui/material';
import {PlayArrow} from '@mui/icons-material';
import {Pallete} from '../../theme/bluebird';
import {useOffice} from '../../contexts/office/context';
import {ScreenPaths} from '../../enums';
import {getAreWeRunningInMobile} from '../../utils/functions';
import {appSizes} from '../../utils/constants';
import {useAuth} from '../../contexts/auth/context';
import {useAppContext} from '../../contexts/app/context';

export interface GenericCollapsibleSidePanelProps {
  children: ReactNode;
  topContent?: ReactNode;
}

export const GenericCollapsibleSidePanel = ({
  children,
  topContent,
}: GenericCollapsibleSidePanelProps) => {
  const officeContext = useOffice();
  const appContext = useAppContext();
  const {state: authState} = useAuth();
  let {pathname} = useLocation();
  const isRunningMobile = getAreWeRunningInMobile();
  const dontShowPanel = isRunningMobile && pathname.endsWith(ScreenPaths.Senior_CareAndActivity);
  const isSeniorsPath = pathname.includes(
    `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Seniors}`
  );
  const focusOnSecondaryNavigation = useCallback(() => {
    officeContext.toggleSideNavigationMenuDrawer(isRunningMobile);
    !isRunningMobile && officeContext.collapseDrawer(true, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusOnPrimaryNavigation = useCallback(() => {
    officeContext.toggleSideNavigationMenuDrawer(isRunningMobile);
    !isRunningMobile && officeContext.collapseDrawer(false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExpandClick = () =>
    officeContext.toggleSideNavigationMenuDrawer(
      !officeContext.state.sideNavigationDrawerCollapsed
    );

  useEffect(() => {
    focusOnSecondaryNavigation();
    return () => focusOnPrimaryNavigation();
  }, [pathname, focusOnPrimaryNavigation, focusOnSecondaryNavigation]);

  const topSpacing =
    officeContext.state.pendingTasksVisible &&
    !pathname.includes(ScreenPaths.Entity_Planner_PendingTasks) &&
    !pathname.includes(ScreenPaths.Me_Notifications)
      ? 70 + appContext.state.currentThemeSettings.pendingTasksHeight
      : 70;

  return (
    <Box
      sx={{
        display: dontShowPanel ? 'none' : 'flex',
        position: 'sticky',
        top: `${topSpacing}px`,
        marginRight: '16px',
        width: officeContext.state.sideNavigationDrawerCollapsed
          ? `${appSizes.genericCollapsibleSidePanel.width.closed}px`
          : `${appSizes.genericCollapsibleSidePanel.width.open}px`,
        height: 'fit-content',
        maxHeight: `calc(100vh - ${topSpacing}px)`,
        borderRadius: '5px',
        boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Box
        sx={{
          marginRight: '16px',
          width: officeContext.state.sideNavigationDrawerCollapsed
            ? `${appSizes.genericCollapsibleSidePanel.width.closed}px`
            : `${appSizes.genericCollapsibleSidePanel.width.open}px`,
          borderRadius: '5px',
          maxHeight: `calc(100vh - ${topSpacing}px)`,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <div
          className={`collapse-button-shadow collapse-button-shadow-${
            officeContext.state.sideNavigationDrawerCollapsed ? 'closed' : 'open'
          }`}
        />
        <IconButton
          onClick={handleExpandClick}
          aria-label="collapse"
          size="small"
          sx={{
            position: 'absolute',
            top: '0',
            right: officeContext.state.sideNavigationDrawerCollapsed ? '-13px' : '-15px',
            background: Pallete.blue.base,
            border: `2px solid ${Pallete.white.base}`,
            borderRadius: '24px',
            zIndex: 1,
            '&:hover': {
              background: Pallete.blue.base,
            },
            rotate: officeContext.state.sideNavigationDrawerCollapsed ? '0deg' : '180deg',
            height: '24px',
            width: '24px',
            padding: '6px',
          }}
        >
          <PlayArrow
            htmlColor="var(--dcm-white-base)"
            sx={{
              fontSize: '12px',
            }}
          />
        </IconButton>
        <Collapse
          in={!officeContext.state.sideNavigationDrawerCollapsed}
          collapsedSize="64px"
          orientation="horizontal"
          timeout={{enter: 0, exit: 700}}
        >
          <Card
            className="senior-side-panel-card"
            sx={{
              boxShadow: 'none',
              width: officeContext.state.sideNavigationDrawerCollapsed
                ? `${appSizes.genericCollapsibleSidePanel.width.closed}px`
                : `${appSizes.genericCollapsibleSidePanel.width.open}px`,
              height: 'fit-content',
              transition: 'width 0.2s ease-in-out',
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 0,
                width: officeContext.state.sideNavigationDrawerCollapsed ? '64px' : '200px',
                transition: 'width 0.2s ease-in-out',
                paddingBottom: '8px !important',
              }}
            >
              <Box
                className={
                  officeContext.state.sideNavigationDrawerCollapsed
                    ? 'senior-side-panel-card-collapsed'
                    : 'senior-side-panel-card-expanded'
                }
                sx={{
                  mx: 0,
                  width: officeContext.state.sideNavigationDrawerCollapsed
                    ? `${appSizes.genericCollapsibleSidePanel.width.closed}px`
                    : `${appSizes.genericCollapsibleSidePanel.width.open}px`,
                }}
              >
                {topContent}

                {topContent &&
                  (!officeContext.state.sideNavigationDrawerCollapsed ? (
                    <Divider
                      sx={{
                        width: '188px',
                        mx: '5px',
                        my: '16px',
                      }}
                    />
                  ) : (
                    <Divider variant="fullWidth" sx={{my: '16px'}} />
                  ))}

                <Box
                  sx={{
                    height: 'fit-content',
                    maxHeight: `calc(100vh - ${isSeniorsPath ? 280 + topSpacing : topSpacing}px)`,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  {children}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Collapse>
      </Box>
    </Box>
  );
};
